import {
  DUE_BUCKET_LABELS, PRIORITY_LABELS, dueBucket, formatDate, formatRelativeDay, formatTime, type Priority,
} from '@takumi/shared';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { Badge, Tooltip } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

/** The single most important signal in the CRM: when is the next follow-up. */
export function FollowupBadge({ dueAt, title, compact, showMissing = true }: { dueAt: string | null; title?: string | null; compact?: boolean; showMissing?: boolean }) {
  if (!dueAt) {
    if (!showMissing) return null;
    return (
      <Badge variant="overdue" className="border border-overdue/20 bg-transparent">
        <AlertTriangle className="size-3" />
        אין פולואפ עתידי
      </Badge>
    );
  }
  const bucket = dueBucket(dueAt);
  const variant = bucket === 'overdue' ? 'overdue' : bucket === 'today' ? 'today' : bucket === 'tomorrow' ? 'tomorrow' : 'upcoming';
  const label =
    bucket === 'overdue'
      ? `באיחור · ${formatRelativeDay(dueAt, new Date(), false)}`
      : bucket === 'today'
        ? `היום ${formatTime(dueAt)}`
        : bucket === 'tomorrow'
          ? `מחר ${formatTime(dueAt)}`
          : formatRelativeDay(dueAt, new Date(), false);
  const badge = (
    <Badge variant={variant}>
      <CalendarClock className="size-3" />
      {compact ? DUE_BUCKET_LABELS[bucket] : label}
    </Badge>
  );
  return title ? <Tooltip content={`${title} · ${formatDate(dueAt)} ${formatTime(dueAt)}`}>{badge}</Tooltip> : badge;
}

export function StageBadge({ name, color, className }: { name: string; color: string; className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap', className)}
      style={{ backgroundColor: `${color}1f`, color }}
    >
      <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
      {name}
    </span>
  );
}

const PRIORITY_STYLE: Record<Priority, string> = {
  low: 'bg-line-strong',
  normal: 'bg-matcha-300',
  high: 'bg-gold-500',
  urgent: 'bg-overdue',
};

export function PriorityDot({ priority, withLabel }: { priority: Priority; withLabel?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft" title={`עדיפות ${PRIORITY_LABELS[priority]}`}>
      <span className={cn('size-2 rounded-full', PRIORITY_STYLE[priority])} />
      {withLabel && PRIORITY_LABELS[priority]}
    </span>
  );
}

/** 3px start-edge accent that encodes follow-up urgency on table rows and cards. */
export function urgencyAccent(dueAt: string | null, isOpenLead = true): string {
  if (!dueAt) return isOpenLead ? 'before:bg-overdue/60' : 'before:bg-transparent';
  const b = dueBucket(dueAt);
  return b === 'overdue' ? 'before:bg-overdue' : b === 'today' ? 'before:bg-today' : b === 'tomorrow' ? 'before:bg-tomorrow' : 'before:bg-transparent';
}
